import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ChevronDown, Clock, AlertTriangle, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useRealtimeTick } from "@/lib/realtime";

const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" }) : "");
const trDay = (d) => d.split("-").reverse().join(".");
const isoDay = (off = 0) => new Date(Date.now() + off * 864e5).toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });

export const canWriteAction = (user, c) => user?.role === "koordinator" || (user?.role === "mudur" && user?.branch_id === c.branch_id);

export function ActionForm({ cid, qid, onSaved, testId = "opening-action" }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await http.post(`/opening/${cid}/action`, { qid, action: v }); toast.success("Aksiyon kaydedildi, koordinatöre bildirildi"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Textarea rows={2} value={v} onChange={(e) => setV(e.target.value)} placeholder="Alınan aksiyonu yazın..." className="bg-slate-950/60 border-slate-700" data-testid={`${testId}-input`} />
      <div className="text-right"><Button size="sm" disabled={busy || v.trim().length < 3} onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid={`${testId}-save`}>Aksiyonu Kaydet</Button></div>
    </div>
  );
}

function AnswerRow({ a, c, user, reload }) {
  const no = a.answer === "no";
  return (
    <div className={`rounded-xl border p-3 ${no ? "border-red-500/30 bg-red-500/5" : "border-slate-800 bg-slate-950/40"}`} data-testid={`opening-answer-row-${c.id}-${a.qid}`}>
      <div className="flex items-start gap-2">
        <span className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${no ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>{no ? <X size={13} /> : <Check size={13} />}</span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="text-sm font-medium">{a.text}</div>
          {no && <div className="text-xs text-red-300"><b>Açıklama:</b> {a.note}</div>}
          {no && a.action && <div className="text-xs text-emerald-300 flex gap-1"><Wrench size={12} className="mt-0.5 shrink-0" /><span><b>Aksiyon:</b> {a.action} <span className="text-slate-500">· {a.action_by} {hm(a.action_at)}</span></span></div>}
          {no && !a.action && (canWriteAction(user, c) ? <ActionForm cid={c.id} qid={a.qid} onSaved={reload} testId={`opening-action-${c.id}-${a.qid}`} />
                                                       : <div className="text-[11px] text-amber-300">Müdür aksiyonu bekleniyor</div>)}
        </div>
      </div>
    </div>
  );
}

function ChecklistCard({ c, user, reload }) {
  const [open, setOpen] = useState(false);
  const done = c.status === "done";
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60" data-testid={`opening-report-${c.id}`}>
      <button onClick={() => done && setOpen(!open)} className="w-full flex items-center gap-3 p-4 text-left">
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{c.branch_name} <span className="text-slate-500 font-mono text-xs ml-1">{trDay(c.date)}</span></div>
          <div className="text-xs text-slate-400 mt-0.5">
            {done ? <>Dolduran: <b className="text-slate-200" data-testid={`opening-report-by-${c.id}`}>{c.done_by_name}</b> · {hm(c.done_at)}</> : "Henüz doldurulmadı"}
          </div>
        </div>
        {done ? (
          c.no_count ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 flex items-center gap-1"><AlertTriangle size={11} /> {c.no_count} sorun</span>
                     : <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Sorunsuz</span>
        ) : <span className={`text-[11px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${c.overdue_warned ? "bg-red-500/15 text-red-300 border-red-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
              <Clock size={11} /> {c.overdue_warned ? "Gecikti" : "Bekliyor"}</span>}
        {done && <ChevronDown size={16} className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && <div className="px-4 pb-4 space-y-2">{c.answers.map((a) => <AnswerRow key={a.qid} a={a} c={c} user={user} reload={reload} />)}</div>}
    </div>
  );
}

export function OpeningReports() {
  const { user } = useAuth();
  const admin = ["koordinator", "patron"].includes(user?.role);
  const [f, setF] = useState({ start: isoDay(-6), end: isoDay(0), branch_id: "" });
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/opening"));
  const load = () => http.get("/opening/reports", { params: { ...f, branch_id: f.branch_id || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [f, tick]); // eslint-disable-line
  useEffect(() => { if (admin) http.get("/branches").then((r) => setBranches(r.data)); }, [admin]);
  return (
    <div className="space-y-4" data-testid="opening-reports">
      <div className="flex flex-wrap gap-2 items-end">
        {admin && (
          <select value={f.branch_id} onChange={(e) => setF({ ...f, branch_id: e.target.value })} className="h-9 rounded-md bg-slate-950/50 border border-slate-700 px-3 text-sm" data-testid="opening-filter-branch">
            <option value="">Tüm şubeler</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="w-40 bg-slate-950/50 border-slate-700" data-testid="opening-filter-start" />
        <Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="w-40 bg-slate-950/50 border-slate-700" data-testid="opening-filter-end" />
      </div>
      <div className="space-y-2">
        {rows.map((c) => <ChecklistCard key={c.id} c={c} user={user} reload={load} />)}
        {!rows.length && <div className="text-sm text-slate-500 py-8 text-center" data-testid="opening-reports-empty">Bu aralıkta kayıt yok</div>}
      </div>
    </div>
  );
}
